// src/pages/EssayGuideSection.jsx
//
// Student-facing render of a pack-level essay-structure GUIDE (a `lit_section` with
// sectionKind === 'guide', essay-structure-guide). Shown FIRST on the set-text
// Essays page instead of a timed essay -- pure teaching content: no timer, no submit,
// no AI grading.
//
// Reads content.blocks:
//   intro?          -- optional longText lead-in
//   parts[]         -- the flexible subheadings list (heading/body/image/video),
//                      rendered via the SHARED TechniquePart (same as technique
//                      lessons), so each section carries its own image + video.
//   sampleHeading?  -- optional heading for the worked sample (default "Sample essay")
//   annotatedSample[] -- ordered { id, label, text } chunks: the worked essay with its
//                      PEACE label beside each chunk on wide screens, ABOVE it on
//                      narrow (readable on mobile; label is FREE TEXT, not an enum).
// intro + chunk text render through <RichText> (5b): markdown, with terminology tooltips kept.
import React from 'react';
import RichText from '../components/richText/RichText.jsx';
import { parseYouTubeId } from '../components/YouTubeEmbed.jsx';
import { TechniquePart } from './TechniqueSection.jsx';
import { selectRenderableParts, selectAnnotatedSample } from './techniqueParts.js';
import LessonCard, { LessonCardHeading, ReadMore } from '../components/reader/LessonCard.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

// One worked-sample chunk: label as a small accent tag ABOVE the text on narrow
// screens, BESIDE it (fixed-width column) on wide screens. Text is never cramped into
// a two-column layout on mobile (the label stacks). A label-less chunk still renders
// its text (full width); a text-less chunk renders just the label tag.
function SampleChunk({ chunk }) {
    const label = typeof chunk.label === 'string' ? chunk.label.trim() : '';
    const text = typeof chunk.text === 'string' ? chunk.text : '';
    return (
        <LessonCard className="md:flex md:gap-4">
            {label && (
                <div className="mb-1 md:mb-0 md:w-32 md:flex-shrink-0">
                    <span className="inline-block font-display text-book-accent text-sm border border-book-accent/40 bg-book-accent/5 px-3 py-1.5 font-medium rounded-full">
                        {label}
                    </span>
                </div>
            )}
            {text && (
                <RichText text={text} className="font-serif text-book-text text-base leading-relaxed md:flex-1 min-w-0" />
            )}
        </LessonCard>
    );
}

export default function EssayGuideSection({ content, done = false, onOpenQuizzes, quizCount = 0, lessonOptions }) {
    const blocks = (content && content.blocks) || {};
    const intro = typeof blocks.intro === 'string' ? blocks.intro : '';
    const sampleHeading = (typeof blocks.sampleHeading === 'string' && blocks.sampleHeading.trim())
        ? blocks.sampleHeading.trim() : 'Sample essay';
    const parts = selectRenderableParts(blocks, parseYouTubeId);
    const sample = selectAnnotatedSample(blocks);
    const empty = !intro && parts.length === 0 && sample.length === 0;

    return (
        <div className="max-w-3xl mx-auto pb-12">
            <LessonHeader eyebrow="Essay guide" title={(content && content.title) || 'Essay Guide'}
                blocks={blocks} done={done} />

            {empty && <EmptyNote />}

            {intro && (
                <LessonCard className="mb-4">
                    <ReadMore text={intro}>
                        <RichText text={intro} className="font-accent italic text-book-text text-base leading-relaxed" />
                    </ReadMore>
                </LessonCard>
            )}

            {parts.map((p, i) => <div id={'guide-part-' + i} key={(typeof p.id === 'string' && p.id) || 'part' + i} className="scroll-mt-24">
                <TechniquePart part={p} missionContent={content} lessonOptions={lessonOptions} />
                {onOpenQuizzes && quizCount > 0 && /language corner/i.test(p.heading || '') && (
                    <button className="mb-6 px-4 py-2 rounded-xl border border-book-accent text-book-accent font-display text-sm"
                        onClick={() => onOpenQuizzes('guide-part-' + i)}>Practise this guide · {quizCount} quizzes →</button>
                )}
            </div>)}

            {sample.length > 0 && (
                <div className="mb-4">
                    <LessonCardHeading>{sampleHeading}</LessonCardHeading>
                    <div className="space-y-4">
                        {sample.map((chunk, i) => (
                            <SampleChunk key={(typeof chunk.id === 'string' && chunk.id) || 'chunk' + i} chunk={chunk} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
